import { useMemo } from 'react'

import {
  digObject, matchFilterArrayIncludes, matchFilterBetweenDates, matchFilterNumber, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import formatDate from '@functions/formatDate'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { CampaignInspectionModel, CampaignInspectionRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['campaignInspections']

type CampaignInspectionFilters = {
  agency_id?: number,
  agent_id?: number,
  campaign_id?: number,
  inspection_date_start?: string,
}

type UseCampaignInspectionsOptions = {
  filters?: CampaignInspectionFilters,
  performHttpRequests?: boolean,
  requestOptions?: CampaignInspectionRequestOptions,
}

function useCampaignInspections(options: UseCampaignInspectionsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    agency_id: filterAgencyId,
    agent_id: filterAgentId,
    campaign_id: filterCampaignId,
    inspection_date_start: filterInspectionDateStart,
  } = filters

  const {
    updatedEntities: { campaignInspections: campaignInspectionsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { campaignInspections, campaigns } = useSelector(reduxState => reduxState.entities)

  const filteredCampaignInspections = useMemo(() => {
    const filtered = Object.values(campaignInspections).filter((campaignInspection: CampaignInspectionModel) => {
      const { campaign_id } = campaignInspection
      const campaign = campaigns[campaign_id] || {}
      const { agent_ids = [] } = campaign

      const dateString = digObject(campaignInspection, 'dates.inspection.date')
      const formattedDate = formatDate(dateString, 'dd/MM/yyyy')

      const matchAgencyId = matchFilterNumber(campaign.agency_id, filterAgencyId)
      const matchAgentId = matchFilterArrayIncludes(agent_ids, filterAgentId)
      const matchCampaignId = matchFilterNumber(campaign_id, filterCampaignId)
      const matchInspectionDateStart = matchFilterBetweenDates(formattedDate, filterInspectionDateStart)

      return matchAgencyId && matchAgentId && matchCampaignId && matchInspectionDateStart
    })

    return sortArrayBy(
      filtered,
      'asc',
      (inspection: CampaignInspectionModel) => formatDate(inspection.dates.inspection.date, 'dd/MM/yyyy'),
    )
  }, [campaignInspectionsUpdatedAt, JSON.stringify(filters)])

  const filteredCampaignInspectionsCount = filteredCampaignInspections.length
  const hasFilteredCampaignInspections = !!filteredCampaignInspectionsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredCampaignInspectionsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingCampaignInspections } = useReduxAction(
    'campaignInspections',
    'loadCampaignInspections',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCampaignInspections,
    filteredCampaignInspectionsCount,
    hasFilteredCampaignInspections,
    loading: loadingCampaignInspections,
  }
}

export default useCampaignInspections
